import { Box, Paper, IconButton, Stack, Table, TextField, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, SxProps, Switch, FormControlLabel } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { setAllReviewed, setForecastStatus, setForecastNote, setSelectedTemplateType, setSelectedMetric, setSelectedMonth, setSelectedSite, setMonthlyData } from "../../../../../features/forecastersSlice";
import HistoryIcon from "@mui/icons-material/History";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NumericFormat } from "react-number-format";
import { selectUserToken, setDisplayErrorSnackBar } from "../../../../../features/userSlice";
import { forecastersEndPointLogic } from "../../../../../logic/AssetManagement/Forecasters/forecastersEndPointLogic";
import store from "../../../../../store";
import MetricsByMonthDialogue from "../ForeCasterInfo/MetricsByMonth/MetricsByMonth";
import { calculateNonEditableMonths } from "../../../../../utils/ForcasterUtils";
import { Theme } from "@emotion/react";
import ForecastSkeleton from "./ForecastSkeleton";
import { NonEditableFTD } from './NonEditableMonths/NonEditableFTD';

interface Props {
  LastYearData: any;
  RequiredDataLastMonths: any;
  loading: any;
  setLoading: any;
}

const NewForecastForFTD = ({ LastYearData, RequiredDataLastMonths, loading, setLoading }: Props) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [editableNRPU, setEditableNRPU] = useState<string | null>(null);
  const [editableGrowthTarget, setEditableGrowthTarget] = useState<string | null>(null);
  const [editableAvgNetREv, setEditableAvgNetREv] = useState<string | null>(null);
  const [editableCPAPV, setEditableCPAPV] = useState<string | null>(null);
  const [editableCVR, setEditableCVR] = useState<string | null>(null);
  const [editableProfit, setEditableProfit] = useState<string | null>(null);
  const [editableChurnRate, setEditableChurnRate] = useState<string | null>(null);
  const [editableAcquiredToFtd, setEditableAcquiredToFtd] = useState<string | null>(null);
  const [editableRsP, setEditableRsP] = useState<string | null>(null);
  const [editableRsBrNg, setEditableRsBrNg] = useState<string | null>(null);
  const [forecastRows, setForecastRows] = useState([]) as any;
  const [showOnlyForecasts, setShowOnlyForecasts] = useState(false);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const [currentDate] = useState(new Date());
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTemplateType = useSelector((state: any) => state?.forecasters?.selectedTemplateType);
  const selectedYear = searchParams.get("selectedYear") as any;
  const forecastLastTwelveMonthsData = useSelector((state: any) => state?.forecasters?.forecastLastTwelveMonthsData);
  const selectedSite = searchParams.get("selectedSite") as any;
  const { monthlyData } = useSelector((state: any) => state?.forecasters);
  const forecastNote = useSelector((state: any) => state?.forecasters?.forcastNote);
  const forecastStatus = useSelector((state: any) => state.forecasters.forecastStatus);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Helper function to get month name
  const getMonthName = (monthIndex: any) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthIndex];
  };

  const LastData = forecastLastTwelveMonthsData?.query_output || [];
  const rrActiveUsersList = Math.round(
    LastData?.filter((entry: any) => entry.month.includes("RR")).map(
      (entry: any) => entry.active_users
    )
  );

  const getQuarterStartDate = (month: number, year: number) => {
    const quarterStartMonth = Math.floor(month / 3) * 3; // Calculate the first month of the quarter
    return new Date(year, quarterStartMonth, 15); // Return the 15th of the first month of the quarter
  };

  const isMonthPassed = (rowMonth: string) => {
    if (!rowMonth || !rowMonth.includes("-")) {
      return false; // Return false if rowMonth is undefined or not in the expected format
    }
    const [, month] = rowMonth.split("-").map(Number);
    if (isNaN(month)) {
      return false; // Return false if month is not a valid number
    }
    const quarterStartDate = getQuarterStartDate(month - 1, selectedYear);
    const currentDate = new Date();
    return currentDate > quarterStartDate;
  };

  const NonEditableMonths = useMemo(() => {
    return calculateNonEditableMonths(
      RequiredDataLastMonths,
      monthlyData,
      monthNames,
      selectedYear
    );
  }, [RequiredDataLastMonths, monthlyData, monthNames, selectedYear]);

  const isMonthEditable = (monthName: string) => {
    const currentDate = new Date();
    const monthIndex = monthNames.indexOf(monthName);
    const currentYear = currentDate.getFullYear();

    // If selected year is future year, all months are editable
    if (Number(selectedYear) > currentYear) {
      return true;
    }

    // If selected year is past year, no months are editable
    if (Number(selectedYear) < currentYear) {
      return false;
    }

    // For current year, apply the quarter logic
    const currentQuarter = Math.floor(
      currentDate.getDate() > 15
        ? currentDate.getMonth() / 3
        : (currentDate.getMonth() - 1) / 3
    );

    // Block all forecasts if we're in Q4 after 15th Oct of the selected year
    if (currentDate.getMonth() >= 9 && currentDate.getDate() > 15) {
      return false;
    }

    const lockedMonths = (currentQuarter + 1) * 3;
    return monthIndex >= lockedMonths;
  };

  // Function to calculate initial values for each month
  const calculateInitialValues = (month: string, monthlyDataParam?: { [key: string]: any }) => {
    // Use passed monthlyData if available, otherwise use state
    const currentMonthlyData = monthlyDataParam || monthlyData;

    let previousMonthActiveUsers;
    const monthIndex = monthNames.indexOf(month);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();
    const selectedYearNum = Number(selectedYear);
    const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

    const monthNum = monthIndex + 1;
    const paddedMonth = monthNum.toString().padStart(2, "0");
    const monthStr = `${selectedYear}-${paddedMonth}`;
    const isPassed = isMonthPassed(monthStr);

    // Calculate previous month's active users with cascading effect
    if (isPassed) {
      previousMonthActiveUsers = rrActiveUsersList;
    } else {
      if (monthIndex === 0) {
        previousMonthActiveUsers = rrActiveUsersList;
      } else {
        const prevMonthName = monthNames[monthIndex - 1];
        const prevMonthStr = `${selectedYear}-${monthIndex
          .toString()
          .padStart(2, "0")}`;
        const isPrevMonthPassed = isMonthPassed(prevMonthStr);

        if (isPrevMonthPassed) {
          if (isEarlyQ3 && monthIndex === 6) {
            const juneData = LastData.find((entry: any) => {
              const [year, month] = entry.month.split("-").map(Number);
              return month === 6 && year === selectedYearNum;
            });
            previousMonthActiveUsers =
              juneData?.active_users || rrActiveUsersList;
          } else {
            previousMonthActiveUsers = rrActiveUsersList;
          }
        } else {
          // Use the previously calculated month's active users
          const prevMonthData = currentMonthlyData[prevMonthName];
          previousMonthActiveUsers =
            prevMonthData?.activeUsers || rrActiveUsersList;
        }
      }
    }

    const averageGrowth = forecastRows[0]?.averageGrowth || 0;
    const averageChurnRate = forecastRows[0]?.averageChurnRate || 0;
    const averageRatioBetweenAcquiredToFtd = forecastRows[0]?.averageRatioAcquiredToFtd || 0;
    const averageNetRevPerActiveUser = forecastRows[0]?.averageAvgNetRevPerActiveUser; // corrency value
    const averageRsP = forecastRows[0]?.averageAvgRsP || 0;
    const averageRsBrNg = forecastRows[0]?.averageRsBrNg || 0;
    const averageCpaPv = forecastRows[0]?.averageCpaPv || 0; // currency value
    const averageCVR = forecastRows[0]?.averageCVR || 0;
    const averageProfit = forecastRows[0]?.averageProfit || 0;
    const averageFTD = forecastRows[0]?.averageFtd || 0;
    const averageCAC = forecastRows[0]?.averageCAC || 0;
    const oldActiveUsers = Math.round((1 - averageChurnRate) * Math.round(previousMonthActiveUsers));
    let ftd;
    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      ftd = averageFTD;
    } else {
      ftd = Math.round(
        ((1 / (1 - averageChurnRate) - 1) * oldActiveUsers + averageGrowth * Math.round(previousMonthActiveUsers)) / averageRatioBetweenAcquiredToFtd
      );
    }
    const acquiredUsers = averageRatioBetweenAcquiredToFtd * ftd;
    const activeUsers = oldActiveUsers + acquiredUsers;
    const estNetRev = averageNetRevPerActiveUser * activeUsers * averageRsBrNg; // currency value
    const estRs = estNetRev * averageRsP; // currency value
    const rsPv = estRs / ftd; // currency value
    const estCpaIncome = averageCpaPv * ftd; // currency value
    const cvr = averageCVR;
    const totalIncome = estCpaIncome + estRs; // currency value
    let profit, spend, cac;
    if (selectedTemplateType === 1 || selectedTemplateType === 2) {
      profit = averageProfit;
      spend = totalIncome - profit;
      cac = spend / ftd;
    } else {
      cac = averageCAC;
      spend = cac * ftd;
      profit = totalIncome - spend;
    }
    let growth;
    if (selectedTemplateType === 1) {
      growth = averageGrowth;
    } else {
      growth = activeUsers / Math.round(previousMonthActiveUsers) - 1;
    }
    const clicks = Math.round(ftd / cvr);
    const epcCpa = estCpaIncome / clicks;
    const epcRs = estRs / clicks;
    const epc = epcCpa + epcRs;
    const cpc = spend / clicks;
    return {
      growth: growth,
      cpaPv: averageCpaPv,
      avgNetRevPerActiveUser: averageNetRevPerActiveUser,
      churnRate: averageChurnRate,
      ratioBetweenAcquiredToFtd: averageRatioBetweenAcquiredToFtd,
      avgRsP: averageRsP,
      rsBrToRsActiveRatio: averageRsBrNg,
      cvr: averageCVR,
      profit,
      ftd,
      oldActiveUsers,
      acquiredUsers,
      rsPv,
      estRs,
      estCpaIncome,
      clicks,
      cac,
      spend,
      totalIncome,
      epcCpa,
      epcRs,
      epc,
      cpc,
      activeUsers,
      estNetRev,
      nrpu: 0, // Default NRPU to 0
    };
  };

  // Determine which months are locked based on current date
  const determineLockedMonths = (date: any) => {
    const month = date.getMonth(); // 0-11
    const day = date.getDate();

    // Until January 15: All 12 months editable
    if (month === 0 && day <= 15) {
      return Array(12).fill(false);
    }
    // January 16 - April 15: First 3 months locked
    else if (
      (month === 0 && day > 15) ||
      (month >= 1 && month < 3) ||
      (month === 3 && day <= 15)
    ) {
      return [...Array(3).fill(true), ...Array(9).fill(false)];
    }
    // April 16 - July 15: First 6 months locked
    else if (
      (month === 3 && day > 15) ||
      (month >= 4 && month < 6) ||
      (month === 6 && day <= 15)
    ) {
      return [...Array(6).fill(true), ...Array(6).fill(false)];
    }
    // July 16 - October 15: First 9 months locked
    else if (
      (month === 6 && day > 15) ||
      (month >= 7 && month < 9) ||
      (month === 9 && day <= 15)
    ) {
      return [...Array(9).fill(true), ...Array(3).fill(false)];
    }
    // October 16 - December 31: All 12 months locked
    else {
      return Array(12).fill(true);
    }
  };

  // Calculate average metrics based on lookback periods
  const calculateAverages = (data: any) => {
    if (!data || data.length === 0) return {};
    const currentMonth = getMonthName(currentDate.getMonth());
    // Sort and filter data excluding 'RR' entries
    const actualData = data
      .filter(
        (item: any) =>
          !item.month.includes("RR") && item.displayMonth !== currentMonth
      )
      .sort((a: any, b: any) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateB.getTime() - dateA.getTime();
      });

    // Calculate averages using the filtered data
    const averages = {
      averageGrowth: calculateAverage(actualData, "growth", 6),
      averageCVR: calculateAverage(actualData, "CVR", 6),
      averageAvgNetRevPerActiveUser: calculateAverage(
        actualData,
        "avg_net_rev_per_active_user",
        6
      ),
      averageChurnRate: calculateAverage(actualData, "churn_rate", 3),
      averageRatioAcquiredToFtd: calculateAverage(
        actualData,
        "Ratio_between_acquired_to_ftd",
        3
      ),
      averageAvgRsP: calculateAverage(actualData, "avg_rs_p", 3),
      averageRsBrNg: calculateAverage(actualData, "rs_br_ng", 3),
      averageProfit: calculateAverage(actualData, "profit", 3),
      averageFtd: calculateAverage(actualData, "ftd", 3),
      averageCAC: calculateAverage(actualData, "CAC", 3),
      averageSpend: calculateAverage(actualData, "spend", 3),
      averageCpaPv: actualData[0]?.cpa_pv || 0,
    };

    return averages;
  };
  // Helper function to calculate average of a specific metric
  const calculateAverage = (data: any, metric: string, monthCount: number) => {
    // Take only the specified number of months
    const relevantData = data.slice(0, monthCount);
    // Filter out null/undefined values but keep zeros
    const validValues = relevantData
      .map((item: any) => item[metric])
      .filter((value: any) => value !== null && value !== undefined);

    if (validValues.length === 0) return 0;
    const sum = validValues.reduce((acc: number, val: number) => acc + val, 0);
    return sum / validValues.filter((value: any) => value !== 0).length;
  };

  // Generate forecast rows with calculated averages
  useEffect(() => {
    setIsDataInitialized(false); // Reset initialization state when data changes

    if (!LastYearData) {
      return; // Wait for LastYearData to be available
    }

    if (LastYearData.length === 0) {
      createEmptyForecastRows();
      return;
    }

    createForecastRowsWithData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LastYearData?.length]); // Add LastYearData as dependency

  const createEmptyForecastRows = () => {
    const lockedMonths = getLockedMonths();
    const emptyAverages = {
      averageGrowth: 0,
      averageCVR: 0,
      averageAvgNetRevPerActiveUser: 0,
      averageChurnRate: 0,
      averageRatioAcquiredToFtd: 0,
      averageAvgRsP: 0,
      averageRsBrNg: 0,
      averageProfit: 0,
      averageFtd: 0,
      averageCAC: 0,
      averageSpend: 0,
      averageCpaPv: 0,
    };
    createAndSetRows(lockedMonths, emptyAverages);
  };

  const createForecastRowsWithData = () => {
    const lockedMonths = getLockedMonths();
    const averages = calculateAverages(LastYearData);
    createAndSetRows(lockedMonths, averages);
  };

  const getLockedMonths = () => {
    const currentYear = new Date().getFullYear();
    const selectedYearNum = Number(selectedYear);
    return selectedYearNum === currentYear
      ? determineLockedMonths(currentDate)
      : Array(12).fill(false);
  };

  const createAndSetRows = (
    lockedMonths: boolean[],
    averages: Record<string, any>
  ) => {
    const rows = Array.from({ length: 12 }, (_, index) => ({
      displayMonth: getMonthName(index),
      isLocked: lockedMonths[index] ? "Yes" : "No",
      ...averages,
    }));

    setForecastRows(rows);
  };

  // Initialize monthly data when forecast rows are available
  useEffect(() => {
    if (forecastRows.length > 0) {
      const editableRows = forecastRows.filter(
        (row: any) => row.isLocked === "No"
      );
      const initialData: { [key: string]: any } = {};

      // If there are editable rows, process them
      if (editableRows.length > 0) {
        editableRows.forEach((row: any) => {
          // Calculate values for each month sequentially
          const monthData = calculateInitialValues(
            row.displayMonth,
            initialData
          );
          initialData[row.displayMonth] = {
            ...monthData,
            displayMonth: row.displayMonth,
          };
        });
      } else {
        // If all months are locked (e.g., after Oct 15), create empty structure to show non-editable months
        // This ensures that the condition Object.keys(monthlyData).length <= 0 doesn't block the table rendering
        // The actual data will be shown via NonEditableMonthsRows component
        const emptyData: { [key: string]: any } = {};
        monthNames.forEach((monthName) => {
          emptyData[monthName] = { displayMonth: monthName };
        });
        initialData.placeholder = { displayMonth: "placeholder" }; // Add a placeholder to avoid empty object
      }

      dispatch(setMonthlyData(initialData));
      setIsDataInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastRows]);

  // Function to calculate values based on changes
  const calculateValues = (month: string, newValues: any, forcePreviousMonthActiveUsers?: number) => {
    const data = { ...monthlyData[month], ...newValues };
    // Get the previous month's active users based on the month name
    let previousMonthActiveUsers;
    const monthIndex = monthNames.indexOf(month);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    // const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();
    const selectedYearNum = Number(selectedYear);

    // If forcePreviousMonthActiveUsers is provided, use that value
    if (forcePreviousMonthActiveUsers !== undefined) {
      previousMonthActiveUsers = forcePreviousMonthActiveUsers;
    } else {
      // Determine if we're in early Q3 (July 1-15)
      const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

      // Check if the month is passed according to isMonthPassed
      const monthNum = monthIndex + 1;
      const paddedMonth = monthNum.toString().padStart(2, "0");
      const monthStr = `${selectedYear}-${paddedMonth}`;
      const isPassed = isMonthPassed(monthStr);
      if (isPassed) {
        // For passed months, use the RR data
        previousMonthActiveUsers = rrActiveUsersList;
      } else {
        // For future months, use cascading calculation
        if (monthIndex === 0) {
          // January is the first month, use RR data
          previousMonthActiveUsers = rrActiveUsersList;
        } else {
          // For other months, use the previous month's calculated active_users
          const prevMonthName = monthNames[monthIndex - 1];

          // Check if previous month is passed or already calculated
          const prevMonthStr = `${selectedYear}-${monthIndex
            .toString()
            .padStart(2, "0")}`;
          const isPrevMonthPassed = isMonthPassed(prevMonthStr);

          if (isPrevMonthPassed) {
            // If previous month is passed, use data from LastYearData
            // For Scenario 2: If we're in early Q3 (July 1-15), use June's data from last 12 month forecast
            if (isEarlyQ3 && monthIndex === 6) {
              // July
              const juneData = LastData.find((entry: any) => {
                const [year, month] = entry.month.split("-").map(Number);
                return month === 6 && year === selectedYearNum; // June of selected year
              });
              previousMonthActiveUsers =
                juneData?.active_users || rrActiveUsersList;
            } else {
              previousMonthActiveUsers = rrActiveUsersList;
            }
          } else if (monthlyData[prevMonthName]) {
            // If previous month is already calculated, use its active_users
            previousMonthActiveUsers = monthlyData[prevMonthName].activeUsers;
          } else {
            // Fallback to RR data if previous month data is not available
            previousMonthActiveUsers = rrActiveUsersList;
          }
        }
      }
    }
    // Always recalculate oldActiveUsers based on the provided or calculated previousMonthActiveUsers
    data.oldActiveUsers = Math.round((1 - data.churnRate) * Math.round(previousMonthActiveUsers));

    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      // For templates 2 and 3, FTD is fixed and growth is calculated
      data.growth = (data.oldActiveUsers + data.acquiredUsers) / Math.round(previousMonthActiveUsers) - 1;
    } else {
      // For template 1, growth is fixed and FTD is calculated
      data.ftd = Math.round(
        ((1 / (1 - data.churnRate) - 1) * data.oldActiveUsers +
          data.growth * Math.round(previousMonthActiveUsers)) /
        data.ratioBetweenAcquiredToFtd
      );
    }

    // Recalculate all dependent values
    data.acquiredUsers = data.ratioBetweenAcquiredToFtd * data.ftd;
    data.activeUsers = data.oldActiveUsers + data.acquiredUsers;

    // Ensure growth is consistent with activeUsers change for all templates
    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      data.growth = data.activeUsers / Math.round(previousMonthActiveUsers) - 1;
    }

    data.estNetRev = data.avgNetRevPerActiveUser * data.activeUsers * data.rsBrToRsActiveRatio;
    data.estRs = data.estNetRev * data.avgRsP;
    data.rsPv = data.estRs / data.ftd;
    data.estCpaIncome = data.cpaPv * data.ftd;
    data.totalIncome = data.estCpaIncome + data.estRs;
    data.clicks = Math.round(data.ftd / data.cvr);

    if (selectedTemplateType === 1 || selectedTemplateType === 2) {
      // For templates 1 and 2, profit is fixed and spend/cac are calculated
      data.spend = data.totalIncome - data.profit;
      data.cac = data.spend / data.ftd;
    } else {
      // For template 3, CAC is fixed and profit/spend are calculated
      data.spend = data.cac * data.ftd;
      data.profit = data.totalIncome - data.spend;
    }

    data.epcRs = data.estRs / data.clicks;
    data.epcCpa = data.estCpaIncome / data.clicks;
    data.epc = data.epcCpa + data.epcRs;
    data.cpc = data.spend / data.clicks;
    data.nrpu = newValues.nrpu !== undefined ? newValues.nrpu : data.nrpu || 0;
    return data;
  };

  // Handle input changes and recalculate values
  const handleInputChange = (month: string, field: string, value: number) => {
    let updatedMonthlyData = { ...monthlyData }; // Start with the changed month
    const monthIndex = monthNames.indexOf(month);
    updatedMonthlyData[month] = calculateValues(month, { [field]: value }); // Update the current month first

    for (let i = monthIndex + 1; i < monthNames.length; i++) {
      // Recalculate all subsequent months
      const nextMonth = monthNames[i];
      if (updatedMonthlyData[nextMonth]) {
        // Only update if the month exists in monthlyData
        const prevMonth = monthNames[i - 1]; // Get the previous month and its active users
        const prevMonthActiveUsers = updatedMonthlyData[prevMonth].activeUsers;
        updatedMonthlyData[nextMonth] = calculateValues(
          nextMonth,
          updatedMonthlyData[nextMonth],
          prevMonthActiveUsers
        ); // Recalculate with the forced previous month's active users
      }
    }
    dispatch(setMonthlyData(updatedMonthlyData));
  };

  // Individual field change handlers
  const onGrowthChange = (e: any, month: string) => {
    handleInputChange(month, "growth", parseFloat(e.target.value));
  };

  const onRatioAcquiredToFtdChange = (e: any, month: string) => {
    handleInputChange(
      month,
      "ratioBetweenAcquiredToFtd",
      parseFloat(e.target.value)
    );
  };

  const onAvgNetRevPerActiveUserChange = (e: any, month: string) => {
    handleInputChange(
      month,
      "avgNetRevPerActiveUser",
      parseFloat(e.target.value)
    );
  };

  const onCpaPvChange = (e: any, month: string) => {
    handleInputChange(month, "cpaPv", parseFloat(e.target.value));
  };

  const onCvrChange = (e: any, month: string) => {
    handleInputChange(month, "cvr", parseFloat(e.target.value));
  };

  const onProfitChange = (e: any, month: string) => {
    handleInputChange(month, "profit", parseFloat(e.target.value));
  };

  const onAvgRsPChange = (e: any, month: string) => {
    handleInputChange(month, "avgRsP", parseFloat(e.target.value));
  };

  const onRsBrToRsActiveRatio = (e: any, month: string) => {
    handleInputChange(month, "rsBrToRsActiveRatio", parseFloat(e.target.value));
  };

  const onChurnRateChange = (e: any, month: string) => {
    handleInputChange(month, "churnRate", parseFloat(e.target.value));
  };

  const onNrpuChange = (e: any, month: string) => {
    handleInputChange(month, "nrpu", parseFloat(e.target.value) || 0);
  };

  const tableheaders = [
    { id: 0, KEY: "month", label: "Month", sortable: true },
    { id: 1, KEY: "nrpu", label: "NRPU", sortable: true },
    { id: 2, KEY: "growth", label: "Growth Target", sortable: true },
    { id: 3, KEY: "avg_net_rev_per_active_user", label: "Avg Net Rev To Active Users", sortable: true },
    { id: 4, KEY: "cpa_pv", label: "CPA PV", sortable: true },
    { id: 5, KEY: "CVR", label: "CVR", sortable: true },
    { id: 6, KEY: "profit", label: "Profit", sortable: true },
    { id: 7, KEY: "active_users", label: "Active Users", sortable: true },
    { id: 8, KEY: "old", label: "Old Active Users", sortable: true },
    { id: 9, KEY: "aquired", label: "Aquired Users", sortable: true },
    { id: 10, KEY: "ftd", label: "FTD", sortable: true },
    { id: 11, KEY: "net_rev", label: "Net Revenue", sortable: true },
    { id: 12, KEY: "rs_income_dollar", label: "RS", sortable: true },
    { id: 13, KEY: "rs_pv", label: "RS PV", sortable: true },
    { id: 14, KEY: "cpa_income_dollar", label: "CPA Income", sortable: true },
    { id: 15, KEY: "total_income", label: "Total Income", sortable: true },
    { id: 16, KEY: "clicks", label: "Clicks", sortable: true },
    { id: 17, KEY: "EPC_CPA", label: "EPC CPA", sortable: true },
    { id: 18, KEY: "EPC_RS", label: "EPC RS", sortable: true },
    { id: 19, KEY: "EPC", label: "EPC", sortable: true },
    { id: 20, KEY: "CPC", label: "CPC", sortable: true },
    { id: 21, KEY: "CAC", label: "CAC", sortable: true },
    { id: 22, KEY: "spend", label: "Spend", sortable: true },
    { id: 23, KEY: "churn_rate", label: "Churn Rate", sortable: true },
    { id: 24, KEY: "ratio_between_acquired_to_ftd", label: "Aquired to FTD", sortable: true },
    { id: 25, KEY: "avg_rs_deal_p", label: "Avg RS Percent", sortable: true },
    { id: 26, KEY: "br_to_ng_active_rs_ratio", label: "RS BR to BR Active Percent", sortable: true },
  ];

  const handleCreateForecast = async (status: "draft" | "publish") => {
    try {
      setLoading(true);
      // Transform forecastRows into the desired payload structure
      const forecastData = Object.entries(monthlyData)
        .filter(([month]) => isMonthEditable(month))
        .map(([month, data]) => {
          const monthIndex = monthNames.indexOf(month) + 1;
          const paddedMonth = monthIndex.toString().padStart(2, "0");
          const typedData = data as any;
          return {
            month: `${selectedYear}-${paddedMonth}-01`,
            spend: typedData.spend,
            clicks: typedData.clicks,
            cpc: typedData.cpc,
            ftd: Number(typedData.ftd.toFixed(0)),
            cvr: Number(typedData.ftd.toFixed(0)) / typedData.clicks,
            cac: typedData.cac,
            profit: typedData.profit,
            cpa: typedData.estCpaIncome,
            rs: typedData.estRs,
            total_income: typedData.totalIncome,
            cpa_pv: typedData.cpaPv,
            rs_pv: typedData.rsPv,
            pv: typedData.cpaPv + typedData.rsPv,
            growth_target: typedData.growth,
            active_users: typedData.activeUsers,
            old_active_users: typedData.oldActiveUsers,
            acquired_active_users: typedData.acquiredUsers,
            churn_rate: typedData.churnRate,
            ratio_between_acquired_to_ftd: typedData.ratioBetweenAcquiredToFtd,
            avg_net_rev_per_active_user: typedData.avgNetRevPerActiveUser,
            net_revenue: typedData.estNetRev,
            avg_rs_deal_p: typedData.avgRsP,
            br_to_ng_active_rs_ratio: typedData.rsBrToRsActiveRatio,
            epc_cpa: typedData.epcCpa,
            epc_rs: typedData.epcRs,
            epc: typedData.epc,
            nrpu: typedData.nrpu || 0,
          };
        });
      const currentDate = new Date(); // Block forecast creation if in Q4 after 15th Oct
      const currentYear = currentDate.getFullYear();
      if (
        Number(selectedYear) === currentYear && ((currentDate.getMonth() === 9 && currentDate.getDate() > 15) || currentDate.getMonth() > 9)
      ) {
        setLoading(false);
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Forecast creation for Q4 is closed after 15th October. You can no longer create or modify forecasts for this period.",
            type: "info",
          })
        );
        return;
      }

      if (!oauthTokenHeader) {
        throw new Error("User doesn't have permissions to create new forecast");
      }

      const response = await forecastersEndPointLogic.createNewForecast(
        oauthTokenHeader,
        {
          year: Number(selectedYear),
          template_type: selectedTemplateType,
          status: status,
          notes: forecastNote,
          forecaster_content: forecastData,
        },
        selectedSite
      );

      if (response?.status === 201) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Forecast Has Been Created Successfully",
            type: "success",
          })
        );
        navigate(`/assets/overview/forecaster?${new URLSearchParams(location.search).toString()}`);
        dispatch(setForecastStatus(null));
        dispatch(setForecastNote(""));
        dispatch(setAllReviewed(false));
        dispatch(setSelectedTemplateType(null));
      }
    } catch (error: any) {
      store.dispatch(setDisplayErrorSnackBar({ message: error?.message || "Error creating forecast", type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forecastStatus) {
      handleCreateForecast(forecastStatus);
      dispatch(setForecastStatus(null));
    }
    // eslint-disable-next-line
  }, [forecastStatus]);

  const formatNumber = (value: any) => {
    if (!value || value === Infinity || value === -Infinity || isNaN(value))
      return "0";
    const roundedValue = Number(Math.round(value));
    return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const [openMetrics, setOpenMetrics] = useState(false);

  const handleClickOpen = (metricKey: string) => {
    setOpenMetrics(true);
    dispatch(setSelectedMetric(metricKey));
    const currentMonth = new Date().getMonth();
    const formattedMonth = `${selectedYear}-0${currentMonth + 1}-01`;
    dispatch(setSelectedMonth(formattedMonth));
    if (selectedSite) {
      dispatch(setSelectedSite(selectedSite));
    } else {
      store.dispatch(
        setDisplayErrorSnackBar(
          "Please select a site. No selected site found in the URL"
        )
      );
    }
  };

  const handleClose = () => {
    setOpenMetrics(false);
  };

  //(R) for the first month of the current quarter (if not passed) (F) for all other future months
  const getMonthIndicator = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    if ((year === currentYear && (month - 1) % 3 === 0)) {
      return "(R)";
    } else {
      return "(F)";
    }
  }

  return (
    <>
      {loading || !isDataInitialized ? (
        <ForecastSkeleton />
      ) : (
        <>
          <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", mt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px" }}>
              <Box>
                <Typography variant="h5">Metrics By Month</Typography>
                <Typography>
                  To create a new forecast, fill in the metrics for every month of
                  the year and save your records.
                </Typography>
              </Box>
              <Box>
                <FormControlLabel control={<Switch color="secondary" checked={showOnlyForecasts} onChange={(e) => setShowOnlyForecasts(e.target.checked)} />} label="Show only forecasts" labelPlacement="start" />
              </Box>
            </Box>
            {showOnlyForecasts && Object.entries(monthlyData).filter(([key]) => key !== "placeholder").length === 0 ? (
              <Stack sx={{ p: 4 }} alignItems="center" justifyContent="center" spacing={2} height={450}>
                <Typography variant="body2" sx={{ color: "#555", whiteSpace: "normal" }}>
                  No data available. Forecast creation for Q4 closed on 15th October, so creating or modifying forecasts for this period is no longer possible.
                </Typography>
              </Stack>
            ) : (
              <>
                <TableContainer className="FTDForecasterTable">
                  <Table aria-label="collapsible table" size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {tableheaders.map((header) => {
                          // Decide which columns should be left-sticky
                          const isLeftSticky =
                            header.id === 0 ||
                            header.id === 1 ||
                            header.id === 2 ||
                            header.id === 3 ||
                            header.id === 4 ||
                            header.id === 5 ||
                            header.id === 6;
                          const leftOffsets: Record<number, number> = {
                            0: 0,
                            1: 109,
                            2: 196,
                            3: 337,
                            4: 564,
                            5: 662,
                            6: 741,
                          };

                          const cellSx: SxProps<Theme> = isLeftSticky
                            ? {
                              position: "sticky",
                              left: leftOffsets[header.id] ?? 0,
                              zIndex: 99,
                              backgroundColor: "#ffffff",
                              borderRight: header.id === 6 ? "1px solid #e0e0e0" : undefined,
                              color: header.id === 0 ? "#000000" : "#9C27B0",
                              padding: header.id === 0 ? "0px 8px 0px 16px!important" : "0px 8px !important",
                              whiteSpace: "nowrap",
                            } : {
                              color: "#000000",
                              padding: header.id === 0 ? "0px 8px 0px 16px!important" : "0px 8px !important",
                              whiteSpace: "nowrap",
                            };

                          return (
                            <TableCell key={header.id} align="left" sx={cellSx}>
                              <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                                {header.label !== "Month" && header.label !== "More" ? (
                                  <IconButton
                                    color="secondary"
                                    size="small"
                                    data-tracker="131"
                                    onClick={() => handleClickOpen(`${header.KEY}`)}
                                  >
                                    <HistoryIcon sx={{ fontSize: "1rem" }} color="secondary" />
                                  </IconButton>
                                ) : null}
                                {
                                  <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px" }}>
                                    {header.label}
                                  </Typography>
                                }
                              </Stack>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!showOnlyForecasts && <NonEditableFTD NonEditableMonths={NonEditableMonths} isMonthPassed={isMonthPassed} formatNumber={formatNumber} />}

                      {Object.values(monthlyData).filter((row: any) => row.displayMonth !== "placeholder").map((row: any, index) => (
                        <React.Fragment key={index}>
                          <TableRow
                            sx={{
                              backgroundColor: isMonthPassed(row.displayMonth) ? "#EBEBEB" : "inherit",
                              "& > *": {
                                borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
                              },
                              height: 35,
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                backgroundColor: "#FFFFFF",
                                padding: "0px 8px 0px 16px !important",
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                              }}
                            >
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <Typography variant="body2">{getMonthIndicator(row.displayMonth)}</Typography>
                                {row.displayMonth ? row.displayMonth : ""}
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                borderBottom: "1px solid #9C27B0",
                                position: "sticky",
                                left: "114px",
                                zIndex: 3,
                              }}
                            >
                              <div onClick={() => setEditableNRPU(row.displayMonth)}
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: "114px",
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableNRPU === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.nrpu || 0}
                                  onValueChange={(values) => {
                                    onNrpuChange(
                                      {
                                        target: {
                                          value: values.floatValue ?? 0,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  suffix="$"
                                  onBlur={() => setEditableNRPU(null)}
                                  thousandSeparator=","
                                  inputProps={{
                                    readOnly: editableNRPU !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s",
                                      ...(editableNRPU ===
                                        row.displayMonth && {
                                        boxShadow:
                                          "0 2px 4px -1px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableNRPU ===
                                            row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableNRPU ===
                                            row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableNRPU ===
                                            row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableNRPU ===
                                            row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableNRPU ===
                                            row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px !important",
                                borderBottom: "1px solid #9C27B0",
                                position: "sticky",
                                left: "201px",
                                zIndex: 3,
                              }}
                            >
                              <div onClick={() => setEditableGrowthTarget(row.displayMonth)}
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: "201px",
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={
                                    editableGrowthTarget === row.displayMonth
                                  }
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.growth * 100}
                                  onValueChange={(values) => {
                                    onGrowthChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  onBlur={() => setEditableGrowthTarget(null)}
                                  thousandSeparator=","
                                  suffix="%"
                                  inputProps={{
                                    readOnly:
                                      editableGrowthTarget !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s",
                                      ...(editableGrowthTarget ===
                                        row.displayMonth && {
                                        boxShadow:
                                          "0 2px 4px -1px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableGrowthTarget ===
                                            row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableGrowthTarget ===
                                            row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableGrowthTarget ===
                                            row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableGrowthTarget ===
                                            row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableGrowthTarget ===
                                            row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                borderBottom: "1px solid #9C27B0",
                                position: "sticky",
                                left: "343px",
                                zIndex: 4,
                              }}
                            >
                              <div onClick={() => setEditableAvgNetREv(row.displayMonth)}
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: "343px",
                                  zIndex: 4,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableAvgNetREv === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.avgNetRevPerActiveUser}
                                  decimalScale={editableAvgNetREv ? 2 : 0}
                                  onValueChange={(values) => {
                                    onAvgNetRevPerActiveUserChange(
                                      { target: { value: values.floatValue } },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableAvgNetREv(null)}
                                  inputProps={{
                                    readOnly: editableAvgNetREv !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  suffix="$"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableAvgNetREv ===
                                        row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableAvgNetREv === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableAvgNetREv === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                        textAlign: "center",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px !important",
                                borderBottom: "1px solid #9C27B0",
                                position: "sticky",
                                left: "571px",
                                zIndex: 5,
                              }}
                            >
                              <div
                                onClick={() => setEditableCPAPV(row.displayMonth)}
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: "571px",
                                  zIndex: 5,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableCPAPV === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.cpaPv}
                                  decimalScale={editableCPAPV ? 2 : 0}
                                  onValueChange={(values) => {
                                    onCpaPvChange(
                                      { target: { value: values.floatValue } },
                                      row.displayMonth
                                    );
                                  }}
                                  onBlur={() => setEditableCPAPV(null)}
                                  thousandSeparator=","
                                  suffix="$"
                                  inputProps={{
                                    readOnly: editableCPAPV !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableCPAPV === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableCPAPV === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableCPAPV === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableCPAPV === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableCPAPV === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableCPAPV === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                borderBottom: "1px solid #9C27B0",
                                position: "sticky",
                                left: "669px",
                                zIndex: 3,
                              }}
                            >
                              <div
                                onClick={() => setEditableCVR(row.displayMonth)}
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: "669px",
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableCVR === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.cvr * 100}
                                  onValueChange={(values) => {
                                    onCvrChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableCVR(null)}
                                  inputProps={{
                                    readOnly: editableCVR !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableCVR === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableCVR === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableCVR === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableCVR === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableCVR === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableCVR === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px !important",
                                borderBottom: "1px solid #9C27B0",
                                position: "sticky",
                                left: "746px",
                                zIndex: 3,
                                borderRight: "1px solid #e0e0e0",
                              }}
                            >
                              <div
                                onClick={() =>
                                  setEditableProfit(row.displayMonth)
                                }
                                style={{
                                  width: "100%",
                                  position: "sticky",
                                  left: "746px",
                                  zIndex: 3,
                                }}
                              >
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableProfit === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.profit}
                                  decimalScale={editableProfit ? 2 : 0}
                                  onValueChange={(values) => {
                                    onProfitChange(
                                      { target: { value: values.floatValue } },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableProfit(null)}
                                  inputProps={{
                                    readOnly: editableProfit !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  suffix="$"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                      ...(editableProfit === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableProfit === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableProfit === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableProfit === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableProfit === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableProfit === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.activeUsers ||
                                row.activeUsers
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.oldActiveUsers ||
                                row.oldActiveUsers
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.acquiredUsers ||
                                row.acquiredUsers
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.ftd || row.ftd
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.estNetRev ||
                                row.estNetRev
                              )}$
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.estRs ||
                                row.rsIncomeDollar
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.rsPv || row.rsPV
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.estCpaIncome ||
                                row.cpaIncomeDollar
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.totalIncome ||
                                row.totalIncome
                              )}$
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.clicks || row.clicks
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.epcCpa || row.epcCpa
                              )}$
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.epcRs || row.epcRs
                              )}$
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.epc || row.epc
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.cpc || row.cpc
                              )}$
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid rgb(207, 206, 206)",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.cac || row.CAC
                              )}$
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                padding: "0px !important",
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #9C27B0",
                              }}
                            >
                              {formatNumber(
                                monthlyData[row.displayMonth]?.spend || row.spend
                              )}
                              $
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                                padding: "0px 8px !important",
                              }}
                            >
                              <div onClick={() => setEditableChurnRate(row.displayMonth)}>
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableChurnRate === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.churnRate * 100}
                                  onValueChange={(values) => {
                                    onChurnRateChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableChurnRate(null)}
                                  inputProps={{
                                    readOnly: editableChurnRate !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableChurnRate === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableChurnRate === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableChurnRate === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableChurnRate === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableChurnRate === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableChurnRate === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                                padding: "0px 8px !important",
                              }}
                            >
                              <div onClick={() => setEditableAcquiredToFtd(row.displayMonth)}>
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableAcquiredToFtd === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.ratioBetweenAcquiredToFtd * 100}
                                  onValueChange={(values) => {
                                    onRatioAcquiredToFtdChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableAcquiredToFtd(null)}
                                  inputProps={{
                                    readOnly: editableAcquiredToFtd !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableAcquiredToFtd === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableAcquiredToFtd === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableAcquiredToFtd === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableAcquiredToFtd === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableAcquiredToFtd === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableAcquiredToFtd === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                                padding: "0px 8px !important",
                              }}
                            >
                              <div onClick={() => setEditableRsP(row.displayMonth)}>
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableRsP === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.avgRsP * 100}
                                  onValueChange={(values) => {
                                    onAvgRsPChange(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableRsP(null)}
                                  inputProps={{
                                    readOnly: editableRsP !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableRsP === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableRsP === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableRsP === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableRsP === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableRsP === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableRsP === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              sx={{
                                backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                                padding: "0px 8px !important",
                              }}
                            >
                              <div onClick={() => setEditableRsBrNg(row.displayMonth)}>
                                <NumericFormat
                                  customInput={TextField}
                                  fullWidth
                                  focused={editableRsBrNg === row.displayMonth}
                                  color="secondary"
                                  defaultValue={monthlyData[row.displayMonth]?.rsBrToRsActiveRatio * 100}
                                  onValueChange={(values) => {
                                    onRsBrToRsActiveRatio(
                                      {
                                        target: {
                                          value: (values.floatValue ?? 0) / 100,
                                        },
                                      },
                                      row.displayMonth
                                    );
                                  }}
                                  thousandSeparator=","
                                  onBlur={() => setEditableRsBrNg(null)}
                                  inputProps={{
                                    readOnly: editableRsBrNg !== row.displayMonth,
                                    style: { textAlign: "center" },
                                  }}
                                  decimalScale={2}
                                  suffix="%"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0px !important",
                                      transition: "transform 0.2s, box-shadow 0.2s",
                                      ...(editableRsBrNg === row.displayMonth && {
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        zIndex: 1,
                                      }),
                                      "& fieldset": {
                                        border:
                                          editableRsBrNg === row.displayMonth
                                            ? "1px solid #9C27B0"
                                            : "none",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          editableRsBrNg === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          editableRsBrNg === row.displayMonth
                                            ? "#9C27B0"
                                            : "none",
                                      },
                                      "& input": {
                                        padding: "8px !important",
                                        color: "#000000DE",
                                        fontSize: "0.875rem",
                                        backgroundColor:
                                          editableRsBrNg === row.displayMonth
                                            ? "#FFFFFF"
                                            : "#FFFFFF",
                                        cursor:
                                          editableRsBrNg === row.displayMonth
                                            ? "text"
                                            : "pointer",
                                        transition: "background-color 0.2s",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center"></Stack>
                </TableContainer>
              </>
            )}
          </Paper>
        </>
      )}
      <MetricsByMonthDialogue open={openMetrics} handleClose={handleClose} selectedSiteFromParent={selectedSite} />
    </>
  )
}

export default NewForecastForFTD
